import { FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE } from 'constants/uiConstants';
import React, { useMemo } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import { formatNumber } from '../Insights/summary2/formatNumber';
import SectionCard from '../Insights/summary2/SectionCard';
import ChartGhostState from './ChartGhostState';
import {
  computeFinancialTrendFactors,
  computeKeyMetricsSummary,
  computeWueTrendFactors,
} from './computeFarmDiagnosticsMetrics';
import TrendChart from './TrendChart';
import TrendKeyMetricsPanel from './TrendKeyMetricsPanel';
import type { FarmDiagnosticsSeries } from './useFarmDiagnosticsSeries';

interface BioVsFinancialDashboardProps {
  series: FarmDiagnosticsSeries;
  showYearOnYear: boolean;
  showRollingAvg: boolean;
  showMarkers: boolean;
  /** Overrides the empty-chart prompt — the plan preview points to the workspace instead of a Yearly Overview it doesn't have. */
  ghostMessage?: string;
}

const formatWue = (v: number) => `${formatNumber(v)} kg/mm`;
const formatFinancialWue = (v: number) => `${formatCurrency(v)}/mm`;

/** Dashboard 4 — WUE and Financial Key Metrics tiles plus WUE Trend and Financial Efficiency charts, side by side, sharing the same years and practice markers. */
const BioVsFinancialDashboard: React.FC<BioVsFinancialDashboardProps> = ({
  series,
  showYearOnYear,
  showRollingAvg,
  showMarkers,
  ghostMessage = FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE,
}) => {
  const wuePoints = useMemo(
    () => series.years.map((y) => ({ year: y.year, value: y.wueIndex })),
    [series.years]
  );
  const financialPoints = useMemo(
    () => series.years.map((y) => ({ year: y.year, value: y.financialWue })),
    [series.years]
  );
  const markers = useMemo(
    () => series.years.map((y) => ({ year: y.year, tags: y.practiceTags })),
    [series.years]
  );

  const latest = series.years[series.years.length - 1];
  const previous = series.years[series.years.length - 2];
  const wueSummary = computeKeyMetricsSummary(
    wuePoints,
    series.wueRollingAvg,
    series.wueTrendByYear
  );
  const financialSummary = computeKeyMetricsSummary(
    financialPoints,
    series.financialWueRollingAvg,
    series.financialWueTrendByYear
  );
  const wueFactors = computeWueTrendFactors(latest, previous);
  const financialFactors = computeFinancialTrendFactors(latest, previous);
  const hasWueData = wuePoints.some((p) => p.value != null);
  const hasFinancialData = financialPoints.some((p) => p.value != null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-6">
        <TrendKeyMetricsPanel
          summary={wueSummary}
          factors={wueFactors}
          formatValue={formatWue}
          yoyUnit="kg/mm"
          metricLabel="WUE Index"
        />
        <TrendKeyMetricsPanel
          summary={financialSummary}
          factors={financialFactors}
          formatValue={formatFinancialWue}
          yoyUnit="$/mm"
          metricLabel="Financial WUE"
        />
      </div>

      <SectionCard id="bioVsFinancial-charts" title="Biological vs Financial Trend" variant="result">
        {/* Stacked, not side by side: each chart keeps TrendChart's own
            max-w-3xl like every other trend chart, and the years line up
            vertically for comparison. */}
        <div className="flex flex-col gap-8">
          <ChartGhostState active={!hasWueData} message={ghostMessage}>
            <TrendChart
              points={wuePoints}
              rollingAvg={series.wueRollingAvg}
              trendByYear={series.wueTrendByYear}
              markers={showMarkers ? markers : []}
              formatValue={formatWue}
              valueLabel="WUE Index (kg/mm)"
              showYearOnYear={showYearOnYear}
              showRollingAvg={showRollingAvg}
            />
          </ChartGhostState>
          <ChartGhostState
            active={!hasFinancialData}
            message={ghostMessage}
          >
            <TrendChart
              points={financialPoints}
              rollingAvg={series.financialWueRollingAvg}
              trendByYear={series.financialWueTrendByYear}
              markers={showMarkers ? markers : []}
              formatValue={formatFinancialWue}
              valueLabel="Financial WUE ($/mm)"
              showYearOnYear={showYearOnYear}
              showRollingAvg={showRollingAvg}
            />
          </ChartGhostState>
        </div>
      </SectionCard>
    </div>
  );
};

export default BioVsFinancialDashboard;
